/* eslint-disable react-refresh/only-export-components -- This file exports a
   route MANIFEST, not components. Its screens are defined inside a factory so
   they can close over the API client, which puts them beyond Fast Refresh's
   reach whatever this file exports. */

/**
 * What the administration area contributes to the route tree.
 *
 * Every route declares the permission it needs, and the same declaration
 * drives both the router's access gate and the navigation menu — so a menu
 * entry cannot appear for a page the user would be refused. Keeping them in
 * one place is what stops the two lists from drifting.
 *
 * None of this is a security control. Each of these screens calls endpoints
 * that re-authorise the request; the gate decides what to navigate to.
 */

import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { AuditScreen } from "./screens/AuditScreen";
import { DashboardScreen } from "./screens/DashboardScreen";
import { RolesScreen } from "./screens/RolesScreen";
import { UsersScreen } from "./screens/UsersScreen";
import "./i18n";

export const ADMIN_PATHS = {
  dashboard: "/admin",
  users: "/admin/users",
  roles: "/admin/roles",
  audit: "/admin/audit",
} as const;

export function adminRoutes(client: ApiClient): FeatureRoutes {
  function Dashboard() {
    return <DashboardScreen client={client} auditHref={ADMIN_PATHS.audit} />;
  }
  function Users() {
    return <UsersScreen client={client} />;
  }
  function Roles() {
    return <RolesScreen client={client} />;
  }
  function Audit() {
    return <AuditScreen client={client} />;
  }

  return {
    id: "admin",
    routes: [
      {
        path: ADMIN_PATHS.dashboard,
        component: Dashboard,
        layout: "app",
        // `any`, not `all`: an administrator who can manage users but not read
        // the audit trail should still reach the overview — the panels they
        // cannot see are simply absent.
        requirement: {
          authenticated: true,
          permissions: ["users.profile.read", "access.role.read", "audit.log.read"],
          mode: "any",
        },
        nav: { labelKey: "admin:nav.dashboard", section: "admin", order: 10 },
      },
      {
        path: ADMIN_PATHS.users,
        component: Users,
        layout: "app",
        requirement: { authenticated: true, permissions: ["users.profile.read"] },
        nav: { labelKey: "admin:nav.users", section: "admin", order: 20 },
      },
      {
        path: ADMIN_PATHS.roles,
        component: Roles,
        layout: "app",
        requirement: { authenticated: true, permissions: ["access.role.read"] },
        nav: { labelKey: "admin:nav.roles", section: "admin", order: 30 },
      },
      {
        path: ADMIN_PATHS.audit,
        component: Audit,
        layout: "app",
        requirement: { authenticated: true, permissions: ["audit.log.read"] },
        nav: { labelKey: "admin:nav.audit", section: "admin", order: 40 },
      },
    ],
  };
}
